/**
 * Presentation payloads - the seam between this package and whatever
 * detection/state pipeline drives it. Hosts map their own scenario/session
 * models onto these and hand them to the `TrainingUxHost`; the package never
 * sees the host's domain types. Ports of `TrainingStepView` /
 * `AssistantCardView`.
 */

export interface TrainingStepView {
  /** 0-based index of this step in the scenario. */
  readonly stepIndex: number;
  /** Total steps in the scenario (drives the progress ticks). 0 = not a step (picker/help forms). */
  readonly stepCount: number;
  /** Form headline. */
  readonly title: string;
  /** Form body text (empty = omitted). */
  readonly description: string;
  /** Action button labels; index 0 renders as the primary button. */
  readonly options: readonly string[];
  /** Opaque image reference from the scenario (the host resolves it to `imageUrl`). */
  readonly imageRef?: string;
  /** The host-resolved image URL for this step. The image area renders only when set. */
  readonly imageUrl?: string;
}

/** A step with no title and no options is a pass-through: no form is shown. */
export function hasPresentation(step: TrainingStepView): boolean {
  return step.title.length > 0 || step.options.length > 0;
}

export interface AssistantCardView {
  /** Stable per-assistant key. Cards are rebuilt only when the set of ids changes. */
  readonly id: string;
  /** Display name, drawn centred on a card with no video and bottom-left over one with. */
  readonly name: string;
  /** Live video track, or null for "connected, nothing to show" (black card). */
  readonly video: MediaStreamTrack | null;
  /** The assistant cannot be heard - draws the muted badge, bottom-right. */
  readonly muted: boolean;
}

/** The maximum number of cards the static grid markup carries (2 x 2). */
export const MAX_ASSISTANT_CARDS = 4;

/** The maximum option buttons the static form markup carries. */
export const MAX_OPTIONS = 4;

/** The maximum progress ticks the static form markup carries. */
export const MAX_TICKS = 12;
